import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Image, Download, Upload, Check, RefreshCw, Monitor } from 'lucide-react';

interface SizeOption {
  size: number;
  label: string;
  checked: boolean;
}

type BgMode = 'transparent' | 'white' | 'custom';

const DEFAULT_SIZES: SizeOption[] = [
  { size: 16, label: '16x16', checked: true },
  { size: 24, label: '24x24', checked: false },
  { size: 32, label: '32x32', checked: true },
  { size: 48, label: '48x48', checked: true },
  { size: 64, label: '64x64', checked: false },
  { size: 128, label: '128x128', checked: false },
  { size: 256, label: '256x256', checked: false },
];

const ImageToIco: React.FC = () => {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [fileName, setFileName] = useState('');
  const [sizes, setSizes] = useState<SizeOption[]>(DEFAULT_SIZES);
  const [bgMode, setBgMode] = useState<BgMode>('transparent');
  const [customColor, setCustomColor] = useState('#ffffff');
  const [cornerRounding, setCornerRounding] = useState(0);
  const [previews, setPreviews] = useState<Map<number, string>>(new Map());
  const [dragOver, setDragOver] = useState(false);
  const [downloadedSize, setDownloadedSize] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectedSizes = sizes.filter(s => s.checked);

  const renderToCanvas = useCallback((img: HTMLImageElement, size: number): HTMLCanvasElement => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;

    // Background
    if (bgMode === 'white') {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, size, size);
    } else if (bgMode === 'custom') {
      ctx.fillStyle = customColor;
      ctx.fillRect(0, 0, size, size);
    }

    // Corner rounding
    if (cornerRounding > 0) {
      const radius = (cornerRounding / 100) * (size / 2);
      ctx.beginPath();
      ctx.moveTo(radius, 0);
      ctx.lineTo(size - radius, 0);
      ctx.quadraticCurveTo(size, 0, size, radius);
      ctx.lineTo(size, size - radius);
      ctx.quadraticCurveTo(size, size, size - radius, size);
      ctx.lineTo(radius, size);
      ctx.quadraticCurveTo(0, size, 0, size - radius);
      ctx.lineTo(0, radius);
      ctx.quadraticCurveTo(0, 0, radius, 0);
      ctx.closePath();
      ctx.clip();
    }

    // Draw image scaled to fit
    const aspect = img.width / img.height;
    let drawW = size, drawH = size, drawX = 0, drawY = 0;
    if (aspect > 1) {
      drawH = size / aspect;
      drawY = (size - drawH) / 2;
    } else if (aspect < 1) {
      drawW = size * aspect;
      drawX = (size - drawW) / 2;
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, drawX, drawY, drawW, drawH);

    return canvas;
  }, [bgMode, customColor, cornerRounding]);

  useEffect(() => {
    if (!image) {
      setPreviews(new Map());
      return;
    }
    const newPreviews = new Map<number, string>();
    for (const s of sizes) {
      if (s.checked) {
        const canvas = renderToCanvas(image, s.size);
        newPreviews.set(s.size, canvas.toDataURL('image/png'));
      }
    }
    setPreviews(newPreviews);
  }, [image, sizes, renderToCanvas]);

  const handleFile = (file: File) => {
    if (!file.type.startsWith('image/')) return;
    setFileName(file.name.replace(/\.[^.]+$/, ''));
    const img = new window.Image();
    img.onload = () => setImage(img);
    img.src = URL.createObjectURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const toggleSize = (index: number) => {
    setSizes(prev => prev.map((s, i) => i === index ? { ...s, checked: !s.checked } : s));
  };

  const downloadPng = (size: number) => {
    if (!image) return;
    const canvas = renderToCanvas(image, size);
    const link = document.createElement('a');
    link.download = `${fileName || 'icon'}-${size}x${size}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    setDownloadedSize(size);
    setTimeout(() => setDownloadedSize(null), 1500);
  };

  const downloadAll = () => {
    if (!image) return;
    for (const s of selectedSizes) {
      const canvas = renderToCanvas(image, s.size);
      const link = document.createElement('a');
      link.download = `${fileName || 'icon'}-${s.size}x${s.size}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    }
  };

  const reset = () => {
    setImage(null);
    setFileName('');
    setSizes(DEFAULT_SIZES);
    setBgMode('transparent');
    setCustomColor('#ffffff');
    setCornerRounding(0);
    setPreviews(new Map());
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-purple-100 rounded-lg">
          <Image className="w-6 h-6 text-purple-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Image to ICO / Favicon</h1>
          <p className="text-gray-500 text-sm">Generate favicon-ready icons at multiple sizes</p>
        </div>
      </div>

      {/* Upload area */}
      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
          dragOver ? 'border-purple-500 bg-purple-50' : 'border-gray-300 hover:border-purple-400'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600 font-medium">Drop an image here or click to upload</p>
        <p className="text-gray-400 text-sm mt-1">PNG, JPG, SVG, WebP</p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
        />
      </div>

      {image && (
        <div className="mt-6 space-y-6">
          {/* Settings row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Size selector */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Icon Sizes</h3>
              <div className="space-y-2">
                {sizes.map((s, i) => (
                  <label key={s.size} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={s.checked}
                      onChange={() => toggleSize(i)}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-gray-700">{s.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Background */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Background</h3>
              <div className="space-y-2">
                {(['transparent', 'white', 'custom'] as BgMode[]).map(mode => (
                  <label key={mode} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="bgMode"
                      checked={bgMode === mode}
                      onChange={() => setBgMode(mode)}
                      className="text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-gray-700 capitalize">{mode}</span>
                  </label>
                ))}
                {bgMode === 'custom' && (
                  <input
                    type="color"
                    value={customColor}
                    onChange={(e) => setCustomColor(e.target.value)}
                    className="w-full h-8 rounded cursor-pointer mt-1"
                  />
                )}
              </div>
            </div>

            {/* Corner rounding */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Corner Rounding</h3>
              <input
                type="range"
                min={0}
                max={100}
                value={cornerRounding}
                onChange={(e) => setCornerRounding(Number(e.target.value))}
                className="w-full accent-purple-600"
              />
              <p className="text-xs text-gray-500 mt-1">{cornerRounding}%</p>
            </div>
          </div>

          {/* Preview grid */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Preview</h3>
            {selectedSizes.length === 0 ? (
              <p className="text-gray-400 text-sm">Select at least one size above</p>
            ) : (
              <div className="flex flex-wrap gap-4">
                {selectedSizes.map(s => {
                  const src = previews.get(s.size);
                  return (
                    <div key={s.size} className="flex flex-col items-center gap-2">
                      <div
                        className="border border-gray-200 rounded-lg flex items-center justify-center bg-white"
                        style={{
                          width: Math.max(s.size + 16, 64),
                          height: Math.max(s.size + 16, 64),
                          backgroundImage: bgMode === 'transparent'
                            ? 'repeating-conic-gradient(#e5e7eb 0% 25%, transparent 0% 50%)'
                            : 'none',
                          backgroundSize: '12px 12px',
                        }}
                      >
                        {src && <img src={src} alt={`${s.size}px`} width={s.size} height={s.size} />}
                      </div>
                      <span className="text-xs text-gray-500">{s.label}</span>
                      <button
                        onClick={() => downloadPng(s.size)}
                        className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1"
                      >
                        {downloadedSize === s.size ? <Check className="w-3 h-3" /> : <Download className="w-3 h-3" />}
                        PNG
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Browser tab mockup */}
          {previews.has(16) && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                <Monitor className="w-4 h-4 inline mr-1" />
                Browser Tab Preview
              </h3>
              <div className="bg-gray-200 rounded-t-lg p-2 inline-block">
                <div className="bg-white rounded-md px-3 py-1.5 flex items-center gap-2 shadow-sm min-w-[200px]">
                  <img src={previews.get(16)} alt="favicon" width={16} height={16} />
                  <span className="text-sm text-gray-700 truncate">{fileName || 'My Website'}</span>
                  <span className="text-gray-400 text-xs ml-auto">x</span>
                </div>
              </div>
              <div className="border border-gray-200 border-t-0 rounded-b-lg p-6 bg-white">
                <div className="h-2 w-3/4 bg-gray-100 rounded mb-2" />
                <div className="h-2 w-1/2 bg-gray-100 rounded" />
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={downloadAll}
              disabled={selectedSizes.length === 0}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium"
            >
              <Download className="w-4 h-4" />
              Download All ({selectedSizes.length} files)
            </button>
            <button
              onClick={reset}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 text-sm font-medium"
            >
              <RefreshCw className="w-4 h-4" />
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageToIco;
